import React, {FunctionComponent, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {isEmpty} from "lodash";
import {Collapse} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import {txt} from "migration/shared/lib/core/i18ngen";
import {useActions} from "migration/shared/lib/hooks/useActions";
import {useTypedSelector} from "migration/shared/lib/hooks/useTypedSelector";
import {useGlobalContext, useUserContext} from "../../../../../../../hooks/globalContext";
import classes from "./IikoHealthCheck.module.scss";
import {IikoActionCreators} from "../../store/action-creators";
import {dishesWithMissingProduct, iikoBindingSources} from "../../store/binding";

enum HealthCheckStatusEnum {
    FAILED = 0,
    SUCCESS = 1,
    WARNING = 2
}

export const IikoHealthCheck: FunctionComponent = () => {
    const {currentLang} = useTypedSelector(state => state.lang);

    const navigate = useNavigate();
    const {i18n} = useGlobalContext();
    const {user, menu, currentMenuId} = useUserContext();

    const iikoState = useTypedSelector(state => state.iiko);
    const {
        iikoApiKey,
        iikoOrganizations,
        iikoMenuSettings,
        iikoRestaurantSections,
        iikoTerminalGroups,
        iikoPaymentTypes,

        errorMsgIikoNomenclature,
        errorMsgIikoOrganizations,
        errorMsgIikoPaymentTypes,
        errorMsgIikoRestaurantSections,
        errorMsgIikoTerminalGroups,

        isLoadingGetIikoApiKey,
        isLoadingGetIikoOrganizations,
        isLoadingGetIikoMenuSettings,
        isLoadingGetNomenclature,
        isLoadingGetIikoExternalMenu,
        isLoadingGetRestaurantSections,
        isLoadingGetIikoTerminalGroups,
        isLoadingGetIikoPaymentTypes
    } = iikoState;
    const {kamiMenuItemsList, menuDataFor, isLoadingGetMenuData} = useTypedSelector(state => state.menuIiko);
    const {
        fetchIikoApiKey,
        fetchIikoOrganizations,
        fetchIikoMenuSettings,
        fetchIikoNomenclature,
        fetchIikoExternalMenu,
        fetchMenuData,
        fetchIikoRestaurantSections,
        fetchIikoTerminalGroups,
        fetchIikoPaymentTypes,
        awakeIikoTerminalGroups,
    } = useActions();

    const [apiKey, setApiKey] = useState({
        name: txt.api_key[currentLang],
        status: HealthCheckStatusEnum.FAILED
    });
    const [org, setOrg] = useState({
        name: txt.organization[currentLang],
        status: HealthCheckStatusEnum.FAILED
    });
    const [tableBinding, setTableBinding] = useState({
        name: txt.table_binding[currentLang],
        status: HealthCheckStatusEnum.FAILED
    });
    const [terminalGroup, setTerminalGroup] = useState({
        name: txt.terminal_group[currentLang],
        status: HealthCheckStatusEnum.FAILED
    });
    const [paymentType, setPaymentType] = useState({
        name: txt.payment_type[currentLang],
        status: HealthCheckStatusEnum.FAILED
    });
    const [selectedTerminalIds, setSelectedTerminalIds] = useState<string[]>([]);
    const [isAwakeLoading, setIsAwakeLoading] = useState<boolean>(false);

    // Fetch data

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoApiKey({organizationId: user?.org?.id || ""}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoOrganizations({organizationId: user?.org?.id || ""}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoMenuSettings({menuId: currentMenuId}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMenuId]);

    // The product check reads IIKO data only through iikoSources, so a cached piece
    // from another menu or a failed fetch never counts.
    const iikoSources = useMemo(() => iikoBindingSources(iikoState, currentMenuId), [iikoState, currentMenuId]);
    const iikoSettings = iikoSources.settings;

    // The source the settings name is fetched once they are loaded for this menu,
    // and again only when the source changes.
    useEffect(() => {
        if (!iikoSettings) return;
        const controller = new AbortController();
        if (iikoSettings.iikoExternalMenuId) {
            fetchIikoExternalMenu({
                organizationId: user?.org?.id || "",
                iikoOrganizationId: iikoSettings.iikoOrganizationId,
                iikoExternalMenuId: iikoSettings.iikoExternalMenuId,
                iikoPriceCategoryId: iikoSettings.iikoPriceCategoryId || null
            }, controller, {navigate: navigate}, true);
        } else {
            fetchIikoNomenclature({
                organizationId: user?.org?.id || "",
                iikoOrganizationId: iikoSettings.iikoOrganizationId
            }, controller, {navigate: navigate}, true);
        }
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iikoSettings?.iikoOrganizationId, iikoSettings?.iikoExternalMenuId, iikoSettings?.iikoPriceCategoryId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchMenuData({menuId: currentMenuId}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMenuId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoRestaurantSections({organizationId: user?.org?.id || ""}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoTerminalGroups({organizationId: user?.org?.id || ""}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchIikoPaymentTypes({organizationId: user?.org?.id || ""}, controller, {navigate: navigate}, true);
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check data

    useEffect(() => {
        if (!isEmpty(iikoApiKey) && iikoApiKey?.isValid) {
            setApiKey(prevState => ({...prevState, status: HealthCheckStatusEnum.SUCCESS}));
        }
    }, [iikoApiKey]);

    useEffect(() => {
        if (!isEmpty(iikoOrganizations) && !isEmpty(iikoMenuSettings)) {
            if (iikoOrganizations?.organizations?.map(org => org.id)?.includes(iikoMenuSettings?.iikoOrganizationId)) {
                setOrg(prevState => ({...prevState, status: HealthCheckStatusEnum.SUCCESS}));
            }
        }
    }, [iikoOrganizations, iikoMenuSettings]);

    useEffect(() => {
        if (!isEmpty(iikoTerminalGroups) && !isEmpty(iikoMenuSettings)) {
            if (iikoTerminalGroups?.terminalGroups?.[iikoMenuSettings?.iikoOrganizationId]?.map(tg => tg.id)?.includes(iikoMenuSettings?.iikoTerminalGroupId)) {
                setTerminalGroup(prevState => ({...prevState, status: HealthCheckStatusEnum.SUCCESS}));
            }
        }
    }, [iikoTerminalGroups, iikoMenuSettings]);

    useEffect(() => {
        if (!isEmpty(iikoPaymentTypes) && !isEmpty(iikoMenuSettings)) {
            if (iikoPaymentTypes?.paymentTypes?.[iikoMenuSettings?.iikoOrganizationId]?.[iikoMenuSettings?.iikoTerminalGroupId]?.map(pt => pt.id)?.includes(iikoMenuSettings?.iikoPaymentTypeId)) {
                setPaymentType(prevState => ({...prevState, status: HealthCheckStatusEnum.SUCCESS}));
            }
        }
    }, [iikoPaymentTypes, iikoMenuSettings]);

    // Dishes placed in the open menu (its own fetch succeeded) whose bound product is
    // missing from the source; the guest page hides them.
    const missingProductDishes = useMemo(
        () => dishesWithMissingProduct(iikoSources, menuDataFor === currentMenuId ? Object.values(kamiMenuItemsList) : null),
        [iikoSources, kamiMenuItemsList, menuDataFor, currentMenuId]
    );
    const productBindingStatus = missingProductDishes === null ? HealthCheckStatusEnum.FAILED
        : missingProductDishes.length ? HealthCheckStatusEnum.WARNING : HealthCheckStatusEnum.SUCCESS;

    useEffect(() => {
        if (!isEmpty(iikoRestaurantSections) && !isEmpty(iikoMenuSettings)) {
            let count = 0;
            Object.keys(iikoMenuSettings?.tableBindings || {}).forEach(placementLSug => {
                iikoRestaurantSections?.restaurantSections?.[iikoMenuSettings?.iikoOrganizationId]?.[iikoMenuSettings?.iikoTerminalGroupId]?.forEach(table => {
                    if (table?.id === iikoMenuSettings?.tableBindings?.[placementLSug]) {
                        count += 1;
                    }
                })
            })

            if (Object.keys(iikoMenuSettings?.tableBindings || {})?.length === count) {
                setTableBinding(prevState => ({...prevState, status: HealthCheckStatusEnum.SUCCESS}));
            }
        }
    }, [iikoRestaurantSections, iikoMenuSettings]);

    const toggleTerminalSelection = (terminalId: string) => {
        setSelectedTerminalIds(prev =>
            prev.includes(terminalId) ? prev.filter(id => id !== terminalId) : [...prev, terminalId]
        );
    };

    const handleAwakeTerminals = async () => {
        if (!user?.org?.id || selectedTerminalIds.length === 0 || !iikoMenuSettings?.iikoOrganizationId) {
            return;
        }
        setIsAwakeLoading(true);
        await awakeIikoTerminalGroups(
            {
                organizationId: user.org.id,
                iikoOrganizationIds: [iikoMenuSettings.iikoOrganizationId],
                terminalGroupIds: selectedTerminalIds,
            },
            {navigate}
        );
        setIsAwakeLoading(false);
    };

    return (
        <div className={classes.main}>
            <div className={classes.list}>
                <HealthCheckItem
                    index={1}
                    name={apiKey.name}
                    loading={isLoadingGetIikoApiKey}
                    status={apiKey.status}
                    reason={iikoApiKey?.isValid ? iikoApiKey?.errorDescription : ""}
                />
                <HealthCheckItem
                    index={2}
                    name={org.name}
                    loading={isLoadingGetIikoOrganizations || isLoadingGetIikoMenuSettings}
                    status={org.status}
                    reason={`${errorMsgIikoOrganizations} \n${errorMsgIikoOrganizations}`}
                />
                <HealthCheckItem
                    index={3}
                    name={terminalGroup.name}
                    loading={isLoadingGetIikoTerminalGroups || isLoadingGetIikoMenuSettings}
                    status={terminalGroup.status}
                    reason={
                        <>
                            {(errorMsgIikoOrganizations || errorMsgIikoTerminalGroups) && (
                                <p>{`${errorMsgIikoOrganizations} \n${errorMsgIikoTerminalGroups}`}</p>
                            )}
                            {!isEmpty(iikoTerminalGroups) && !isEmpty(iikoMenuSettings) && (
                                <div style={{marginTop: 8}}>
                                    <div style={{fontWeight: 500, marginBottom: 4}}>
                                        {(txt as any).iiko_terminals_status_title[currentLang]}
                                    </div>
                                    <ul style={{paddingLeft: 18, marginBottom: 8}}>
                                        {[
                                            ...(iikoTerminalGroups?.terminalGroups?.[iikoMenuSettings?.iikoOrganizationId] || []),
                                            ...(iikoTerminalGroups?.terminalGroupsInSleep?.[iikoMenuSettings?.iikoOrganizationId] || []),
                                        ].map(tg => (
                                            <li key={tg.id}>
                                                <label style={{display: "flex", alignItems: "center", gap: 8, cursor: "pointer"}}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTerminalIds.includes(tg.id)}
                                                        onChange={() => toggleTerminalSelection(tg.id)}
                                                    />
                                                    <span>
                                                        {tg.data.name} ({tg.data.address}) —{" "}
                                                        {tg.data.isAlive
                                                            ? <span style={{color: "green"}}>{(txt as any).iiko_online[currentLang]}</span>
                                                            : <span style={{color: "red"}}>{(txt as any).iiko_offline[currentLang]}</span>}
                                                    </span>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        className="button -primary"
                                        disabled={selectedTerminalIds.length === 0 || isAwakeLoading}
                                        onClick={handleAwakeTerminals}
                                    >
                                        {isAwakeLoading ? "..." : "Пробудить выбранные терминалы"}
                                    </button>
                                </div>
                            )}
                        </>
                    }
                />
                <HealthCheckItem
                    index={4}
                    name={paymentType.name}
                    loading={isLoadingGetIikoPaymentTypes || isLoadingGetIikoMenuSettings}
                    status={paymentType.status}
                    reason={`${errorMsgIikoOrganizations} \n${errorMsgIikoPaymentTypes}`}
                />
                <HealthCheckItem
                    index={5}
                    name={txt.product_binding[currentLang]}
                    loading={isLoadingGetIikoMenuSettings || isLoadingGetNomenclature || isLoadingGetIikoExternalMenu || isLoadingGetMenuData}
                    status={productBindingStatus}
                    reason={missingProductDishes?.length
                        ? `${i18n.iiko_dishes_missing_product()} (${missingProductDishes.length}): ${missingProductDishes.map(dish => dish.name?.[menu?.i18n?.defaultLang]).join(", ")}`
                        : `${errorMsgIikoOrganizations} \n${errorMsgIikoNomenclature}`}
                />
                <HealthCheckItem
                    index={6}
                    name={tableBinding.name}
                    loading={isLoadingGetRestaurantSections || isLoadingGetIikoMenuSettings}
                    status={tableBinding.status}
                    reason={`${errorMsgIikoOrganizations} \n${errorMsgIikoRestaurantSections}`}
                />
            </div>
        </div>
    )
}

interface IHealthCheckItem {
    index: number;
    name: string;
    loading: boolean;
    status: HealthCheckStatusEnum;
    reason?: JSX.Element | string;
}

const HealthCheckItem: FunctionComponent<IHealthCheckItem> = ({index, name, loading, status, reason}) => {
    const {currentLang} = useTypedSelector(state => state.lang);

    const items = [
        {
            key: index,
            label: (
                <div className={classes.list__item}>
                    <div className={classes.list__item__index}>{`${index}.`}</div>
                    <div className={classes.list__item__name}>{name}</div>
                    <div className={classes.check}>
                        {loading
                            ? <div className={classes.loader__wrapper}><LoadingOutlined/></div>
                            : status === HealthCheckStatusEnum.SUCCESS ? "✅"
                                : status === HealthCheckStatusEnum.WARNING ? "⚠️" : "❌"
                        }
                    </div>
                </div>
            ),
            children:
                <p>{(status === HealthCheckStatusEnum.FAILED && !loading && !reason) ? txt.request_cancelled[currentLang] : reason}</p>
        }
    ]

    return (
        <Collapse
            items={items}
            collapsible={(status === HealthCheckStatusEnum.SUCCESS || loading) ? "disabled" : undefined}
            style={{color: "rgba(0, 0, 0, 0.88)"}}
        />
    )
}
